import { Box, Card, CardContent, Typography, Stack, Divider, Alert } from "@mui/material";
import { BeachAccess as VacationIcon, LocalHospital as SickIcon, InfoOutlined as InfoIcon } from "@mui/icons-material";
import React from "react";
import { formatDate } from "../../utils/absenceUtils";

interface BalanceItem {
  label: string;
  available: number;
  used: number;
  total: number;
  icon: React.ReactNode;
  color: string;
}

interface BalanceSummaryProps {
  vacationDaysAvailable: number;
  vacationDaysUsed: number;
  vacationDaysCarryOver?: number;
  sickDaysAvailable: number;
  sickDaysUsed: number;
  vacationEligibilityDate?: string | null;
  accruedVacationDays?: number | null;
  variant?: "card" | "compact";
}

export const BalanceSummary: React.FC<BalanceSummaryProps> = ({
  vacationDaysAvailable,
  vacationDaysUsed,
  vacationDaysCarryOver = 0,
  sickDaysAvailable,
  sickDaysUsed,
  vacationEligibilityDate,
  accruedVacationDays,
  variant = "card",
}) => {
  const totalVacationDays = vacationDaysAvailable + vacationDaysUsed;

  // In the accrual period when accruedVacationDays is non-null
  const isInAccrualPeriod = accruedVacationDays != null;
  // Whole days the employee can actually request right now
  const usableDays = isInAccrualPeriod ? Math.floor(accruedVacationDays!) : null;
  const daysUntilFullAccess = vacationEligibilityDate
    ? Math.ceil((new Date(vacationEligibilityDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  const balanceItems: BalanceItem[] = [
    {
      label: "Vacation Days",
      available: vacationDaysAvailable,
      used: vacationDaysUsed,
      total: totalVacationDays,
      icon: <VacationIcon sx={{ fontSize: 24 }} />,
      color: "#2196F3",
    },
    {
      label: "Sick Days",
      available: sickDaysAvailable,
      used: sickDaysUsed,
      total: sickDaysAvailable + sickDaysUsed,
      icon: <SickIcon sx={{ fontSize: 24 }} />,
      color: "#FF9800",
    },
  ];

  const BalanceItemDisplay = ({ item }: { item: BalanceItem }) => (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Box sx={{ color: item.color }}>{item.icon}</Box>
        <Typography variant="subtitle2" fontWeight={600}>
          {item.label}
        </Typography>
      </Box>
      <Box display="flex" gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="caption" color="text.secondary">
            Available
          </Typography>
          <Typography variant="h6" fontWeight={700} color={item.color}>
            {item.available}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Used
          </Typography>
          <Typography variant="h6">
            {item.used}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Total
          </Typography>
          <Typography variant="h6">
            {item.total}
          </Typography>
        </Box>
      </Box>
      {vacationDaysCarryOver > 0 && item.label === "Vacation Days" && (
        <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
          (includes {vacationDaysCarryOver} carry-over days)
        </Typography>
      )}
    </Box>
  );

  if (variant === "compact") {
    return (
      <Stack spacing={2}>
        {balanceItems.map((item, index) => (
          <React.Fragment key={item.label}>
            <BalanceItemDisplay item={item} />
            {index < balanceItems.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Stack>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Absence Balance
        </Typography>
        
        {/* Vacation Accrual Period Alert */}
        {isInAccrualPeriod && vacationEligibilityDate && (
          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{ mb: 2, fontSize: '0.875rem' }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              Vacation Accrual Period
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              You have accrued <strong>{accruedVacationDays!.toFixed(2)} days</strong> — you can request up to{" "}
              <strong>{usableDays} day{usableDays !== 1 ? "s" : ""}</strong> now.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Full access unlocks on {formatDate(vacationEligibilityDate)}
              {daysUntilFullAccess > 0 && ` (in ${daysUntilFullAccess} days)`}.
            </Typography>
          </Alert>
        )}
        
        <Stack spacing={3} mt={2}>
          {balanceItems.map((item, index) => (
            <React.Fragment key={item.label}>
              <BalanceItemDisplay item={item} />
              {index < balanceItems.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};


